import React, { useState, useEffect, useRef, lazy, Suspense, ReactNode } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useParams, useLocation, useHistory, RouteProps } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { AuthProvider, useAuth } from './components/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ChatProvider } from './contexts/ChatContext';
import { FeedTopBar } from './tiktok/components/FeedTopBar';
import { SideMenu } from './components/SideMenu';
import { FeedBottomNav } from './tiktok/components/FeedBottomNav';
import { SearchOverlay } from './tiktok/components/SearchOverlay';
import { LoadingScreen } from './components/LoadingScreen';
import { initDatabase } from './lib/initDatabase';
import { GoogleMapsProvider } from './components/GoogleMapsProvider';
import { supabase } from './lib/db';
import { UserProfilePreview } from './components/UserProfilePreview';
import { ReferralService } from './services/ReferralService';
import { useDeepLinkListener } from './hooks/useDeepLinkListener';
import { PreviewShare } from './pages/PreviewShare';
import { PreviewSharePage } from './pages/PreviewSharePage';
import { HelmetProvider } from 'react-helmet-async';
import { BookingFlow } from './pages/BookingFlow';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationBell } from './components/NotificationBell';
import NotificationsPage from './pages/NotificationsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import { notificationService } from './services/NotificationService';
import { useAppBadge } from './hooks/useAppBadge';

// TikTok-style feed page
const FeedPage = lazy(() => import('./tiktok/FeedPage').then(module => ({ default: module.FeedPage })));

// Dynamically imported components (classic/legacy pages)
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const Signup = lazy(() => import('./pages/Signup').then(module => ({ default: module.Signup })));
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const Providers = lazy(() => import('./pages/Providers').then(module => ({ default: module.Providers })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Help = lazy(() => import('./pages/Help').then(module => ({ default: module.Help })));
const FAQ = lazy(() => import('./pages/FAQ').then(module => ({ default: module.FAQ })));
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const Cookies = lazy(() => import('./pages/Cookies').then(module => ({ default: module.Cookies })));
const Marketplace = lazy(() => import('./pages/Marketplace').then(module => ({ default: module.Marketplace })));
const ProviderDetails = lazy(() => import('./pages/ProviderDetails').then(module => ({ default: module.ProviderDetails })));
const SellerDetails = lazy(() => import('./pages/SellerDetails').then(module => ({ default: module.SellerDetails })));
const ChatLayout = lazy(() => import('./components/chat/ChatLayout').then(module => ({ default: module.ChatLayout })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const SavedItemsPage = lazy(() => import('./components/SavedItemsPage'));
const Orders = lazy(() => import('./components/Orders').then(module => ({ default: module.Orders })));
const MyPurchases = lazy(() => import('./pages/MyPurchases'));
const StoreOrders = lazy(() => import('./pages/StoreOrders'));
const ItemDetailsFullPage = lazy(() => import('./components/ItemDetailsFullPage').then(module => ({ default: module.ItemDetailsFullPage })));
const SendersTravelers = lazy(() => import('./pages/SendersTravelers').then(module => ({ default: module.SendersTravelers })));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// --- Define Props Interfaces ---
interface CustomRouteProps extends RouteProps {
  children: ReactNode;
}
// ---

// React Router v5 protected route pattern
const ProtectedRoute = ({ children, ...rest }: CustomRouteProps) => {
  const { user } = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        user ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location }
            }}
          />
        )
      }
    />
  );
};

const AuthRoute = ({ children, ...rest }: CustomRouteProps) => {
  const { user } = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        user ? (
          <Redirect
            to={{
              pathname: "/profile",
              state: { from: location }
            }}
          />
        ) : (
          children
        )
      }
    />
  );
};

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const history = useHistory();

  // Sync app icon badge with unread notification + message counts
  useAppBadge();

  // Listen for notification deep-link navigation
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const path = (e as CustomEvent).detail?.path;
      if (path) history.push(path);
    };
    window.addEventListener('app-navigate', handleNavigate);
    return () => window.removeEventListener('app-navigate', handleNavigate);
  }, [history]);

  // Listen for custom event from FeedTopBar to open the SideMenu
  useEffect(() => {
    const handleOpenSideMenu = () => setIsMenuOpen(true);
    window.addEventListener('open-side-menu', handleOpenSideMenu);
    return () => window.removeEventListener('open-side-menu', handleOpenSideMenu);
  }, []);

  // Get current location to determine page type
  const location = useLocation();
  const isFeedPage = location.pathname === '/';
  const isChatPage = location.pathname.startsWith('/chat');

  // Hide navigation only on chat pages
  const hideNavigation = false;

  // Render with universal navigation
  return (
    <div className="app-theme flex flex-col h-screen overflow-hidden relative">
      {/* Global Top Navigation */}
      {!hideNavigation && (
        <FeedTopBar />
      )}

      {/* Side Menu */}
      <SideMenu isOpen={isMenuOpen} setIsOpen={setIsMenuOpen} />

      {/* Main Content */}
      <main
        className={isFeedPage ? 'flex-grow overflow-hidden' : (isChatPage ? 'flex-grow overflow-hidden' : 'flex-grow overflow-y-auto')}
        style={(!isFeedPage && !isChatPage) ? {
          paddingTop: '60px',
          paddingBottom: '60px'
        } : isChatPage ? {
          paddingTop: '60px',
          paddingBottom: '60px'
        } : undefined}
      >
        {children}
      </main>

      {/* Global Bottom Navigation */}
      {!hideNavigation && (
        <FeedBottomNav onSearchOpen={() => setSearchOpen(true)} />
      )}

      {/* Search overlay */}
      <SearchOverlay
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
};

// Use React Router v5 syntax for this component
const UserProfilePreviewWrapper = () => {
  const { userId } = useParams<{ userId?: string }>();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';
  const { user } = useAuth();
  
  // If it's a preview, use the URL parameter regardless of logged-in user
  // Otherwise, check if showing own profile
  const profileIdToUse = isPreview ? userId : (user?.id || undefined);
  
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, contact_name, email, phone, bio, image_url, role')
        .eq('id', profileIdToUse)
        .single();
      if (error) {
        console.error('Error fetching profile:', error);
      } else {
        setProfile(data);
      }
      setLoading(false);
    };

    if (profileIdToUse) {
      fetchProfile();
    }
  }, [profileIdToUse]);

  if (loading) {
    return <p>Loading profile...</p>;
  }

  if (!profile) {
    return <p>Profile not found.</p>;
  }

  return <UserProfilePreview profile={profile} />;
};

// DeepLinkListener to handle product and referral deep links
const DeepLinkListener = () => {
  useDeepLinkListener();
  return null;
};

// Create a separate component for the home route logic
// Desktop/laptop → Marketplace, Mobile → TikTok-style feed
const HomeRoute = () => {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isDesktop ? <Marketplace /> : <FeedPage />;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
 
  useEffect(() => {
    const init = async () => {
      try {
        const success = await initDatabase();
        if (!success) setError('Failed to initialize application');
        
        // Initialize the ReferralService (which handles Branch SDK)
        await ReferralService.getInstance().initialize();

      } catch (err: any) {
        console.error('Initialization error:', err);
        setError('Failed to initialize application');
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  if (isLoading) return <LoadingScreen />;
  if (error) return <div>Error: {error}</div>;

  return (
    <HelmetProvider>
      <Router>
        <GoogleMapsProvider>
          <LanguageProvider>
            <ThemeProvider>
            <AuthProvider>
              <NotificationProvider>
              <ChatProvider>
                <Layout>
                  <Suspense fallback={<LoadingScreen />}>
                    <Switch>
                      {/* Auth routes */}
                      <AuthRoute path="/login">
                        <Login />
                      </AuthRoute>
                      <AuthRoute path="/signup">
                        <Signup />
                      </AuthRoute>
                      <Route exact path="/forgot-password" component={ForgotPassword} />
                      <Route exact path="/reset-password" component={ResetPassword} />
                      
                        {/* Home route - TikTok-style feed */}
                        <Route exact path="/" component={HomeRoute} />
                          <Route exact path="/app">
                            <Redirect to="/" />
                          </Route>

                        {/* Classic home page (legacy) */}
                        <Route exact path="/home-classic" component={Home} />

                      {/* Other routes */}
                      <Route exact path="/providers" component={Providers} />
                      <Route exact path="/services">
                        <Redirect to="/providers" />
                      </Route>
                      <Route exact path="/provider/:id" component={ProviderDetails} />
                      <Route exact path="/seller/:id" component={SellerDetails} />
                      <Route exact path="/marketplace" component={Marketplace} />
                      <Route exact path="/shop">
                        <Redirect to="/marketplace" />
                      </Route>
                      
                      <ProtectedRoute exact path="/profile">
                        <Profile />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/me">
                        <Redirect to="/profile" />
                      </ProtectedRoute>
                      
                      <Route exact path="/contact" component={Contact} />
                      <Route exact path="/help" component={Help} />
                      <Route exact path="/faq" component={FAQ} />
                      <Route exact path="/privacy" component={Privacy} />
                      <Route exact path="/terms" component={Terms} />
                      <Route exact path="/cookies" component={Cookies} />
                      
                        <ProtectedRoute path="/chat">
                        <ChatLayout />
                      </ProtectedRoute>
                        <ProtectedRoute exact path="/inbox">
                          <Redirect to="/chat" />
                        </ProtectedRoute>
                      
                      <Route exact path="/saved-items" component={SavedItemsPage} />
                      <Route exact path="/saved">
                        <Redirect to="/saved-items" />
                      </Route>
                      <ProtectedRoute exact path="/orders">
                        <Redirect to="/purchases" />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/purchases">
                        <MyPurchases />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/store-orders">
                        <StoreOrders />
                      </ProtectedRoute>
                      <ProtectedRoute exact path="/cart">
                        <Redirect to="/purchases" />
                      </ProtectedRoute>
                      <Route exact path="/profile/:userId" component={UserProfilePreviewWrapper} />
                      <Route exact path="/itemDetails/:id" component={ItemDetailsFullPage} />
                      <Route exact path="/senders-travelers" component={SendersTravelers} />
                      <Route exact path="/preview-share" component={PreviewShare} />
                      <Route exact path="/preview-share/:item_id" component={PreviewSharePage} />
                          <Route path="/providers/:provider_id/book" component={BookingFlow} />
                          <Route path="/book/:provider_id" component={BookingFlow} />
                          <ProtectedRoute exact path="/notifications">
                            <NotificationsPage />
                          </ProtectedRoute>
                          <ProtectedRoute path="/appointments">
                            <AppointmentsPage />
                          </ProtectedRoute>
                          <ProtectedRoute exact path="/bookings">
                            <Redirect to="/appointments" />
                          </ProtectedRoute>

                      {/* Catch all route - redirect to home */}
                      <Route path="*">
                        <Redirect to="/" />
                      </Route>
                    </Switch>
                  </Suspense>
                </Layout>
                <Toaster position="bottom-center" />
              </ChatProvider>
              </NotificationProvider>
            </AuthProvider>
            </ThemeProvider>
          </LanguageProvider>
        </GoogleMapsProvider>
      </Router>
    </HelmetProvider>
  );
};

export default App;